// Measure classification — which numbers off a Giraffe section may be summed.
//
// The engine emits ONE baked section per stack carrying `levels: n`, and only
// some of its fields have `n` applied:
//
//     area        1097   <- one level's footprint
//     grossArea   9874   <- nine levels, already multiplied
//     levels         9
//
// Expand that into nine rows carrying the 9x grossArea and you report 81x — a
// number that looks entirely plausible. And salePrice / hardCost / efficiency /
// floorToFloor are rates: $/m2 plus $/m2 is not $/m2.
//
// So every field is classified before anything aggregates it, and an
// unrecognised field defaults to `rate` — the class that declines to sum.
// A measure that refuses to add up is visible and correctable; one that adds up
// when it should not is a wrong number nobody notices. (sdk-pitfalls.md 9)

import type { DataRow, MeasureClass } from './types';

/** Footprint measures: one level's worth, so they sum across sections. */
const PLAN_FIELDS = new Set(['area', 'perimeter']);

/** Whole-stack measures: `levels` is already applied, so they sum as-is. */
const TOTAL_FIELDS = new Set([
  'grossarea',
  'grossbuildingarea',
  'netarea',
  'saleablearea',
  'dwellingtotalcount',
  '_height',
]);

/** Per-unit rates. Never sum; take an area-weighted mean. */
const RATE_FIELDS = new Set([
  'saleprice',
  'hardcost',
  'softcost',
  'efficiency',
  'floortofloor',
  'levels',
  'baseheight',
  '_stackorder',
]);

/**
 * Bookkeeping, not measurement — never offered as a measure. Both
 * `_buildingid` and `buildingid` are listed: Giraffe's own raw spelling is
 * `buildingID` (no underscore, confirmed live), but a baked section may
 * still carry an underscore-prefixed form — this set is matched by plain
 * `.toLowerCase()`, not the underscore-stripping `squashField`, so both
 * spellings need their own entry.
 */
export const NON_MEASURE_FIELDS = new Set([
  'id',
  'groupid',
  '_buildingid',
  'buildingid',
  '_isbasesection',
  '_istopofstack',
  'layerid',
  'geometry',
]);

/**
 * Never totalled is not the same as never grouped by. `layerId` is bookkeeping
 * to a measure but a perfectly good row or column heading in a schedule — the
 * GI.* filters already accept it. The building id the same way, now that the
 * Areas sheet carries a real one per row (see areasTable.ts, field
 * `buildingID`) — an Area Schedule grouping "By Building" is exactly this
 * field as the row header.
 */
const GROUPABLE_NON_MEASURES = new Set(['layerid', '_buildingid', 'buildingid']);

/** Whether a field can head the rows or columns of a schedule. */
export function isGroupable(field: string): boolean {
  const key = field.toLowerCase();
  return !NON_MEASURE_FIELDS.has(key) || GROUPABLE_NON_MEASURES.has(key);
}

/**
 * `dwelling-<mixKey>-Count` keys come from the user's own dwelling mix, so the
 * field list has to be discovered from the data — hardcoding it means a project
 * with a bespoke mix silently loses its unit counts.
 */
/**
 * Name-based fallback works on TOKENS, not on suffixes: `salesRateCount` ends
 * in "count" but is plainly a rate, and a suffix test gets it backwards. Rate
 * tokens are checked first for exactly that reason.
 */
const RATE_TOKENS = new Set([
  'rate', 'price', 'cost', 'efficiency', 'ratio', 'psf', 'psm', 'per', 'yield', 'margin', 'factor',
]);
const TOTAL_TOKENS = new Set(['count', 'unit', 'units', 'total', 'qty', 'quantity']);

/** `dwelling-2BED-Count` -> ['dwelling','2','bed','count']. */
export function tokenizeField(field: string): string[] {
  return field
    .replace(/^_/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((t) => t.toLowerCase());
}

export interface ClassifyOptions {
  /** Rows to sample for type detection. More is slower, not more accurate. */
  sample?: readonly DataRow[];
}

/**
 * Classify one field. Data-driven where it can be (a field whose values are all
 * text is `text`, whatever its name suggests), name-driven where it must be.
 */
export function classifyField(field: string, opts: ClassifyOptions = {}): MeasureClass {
  const key = field.toLowerCase();

  const sampled = opts.sample ? sampleType(field, opts.sample) : 'unknown';
  if (sampled === 'nested') return 'nested';
  if (sampled === 'text') return 'text';

  if (PLAN_FIELDS.has(key)) return 'plan';
  if (TOTAL_FIELDS.has(key)) return 'total';
  if (RATE_FIELDS.has(key)) return 'rate';
  const tokens = tokenizeField(field);
  // Rates first: `salesRateCount` must not become a total because it also
  // carries "count".
  if (tokens.some((t) => RATE_TOKENS.has(t))) return 'rate';
  if (tokens.some((t) => TOTAL_TOKENS.has(t))) return 'total';

  if (sampled === 'none') return 'text';
  // Numeric but unrecognised. Default to the class that refuses to sum.
  return 'rate';
}

type Sampled = 'number' | 'text' | 'nested' | 'none' | 'unknown';

function sampleType(field: string, rows: readonly DataRow[]): Sampled {
  let numbers = 0;
  let texts = 0;
  let nested = 0;
  const limit = Math.min(rows.length, 200);
  for (let i = 0; i < limit; i++) {
    const v = rows[i]!.properties[field];
    if (v === null || v === undefined || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) numbers += 1;
    else if (typeof v === 'boolean') texts += 1;
    else if (typeof v === 'string') {
      const n = Number(v);
      if (v.trim() !== '' && Number.isFinite(n)) numbers += 1;
      else texts += 1;
    } else if (typeof v === 'object') nested += 1;
    else texts += 1;
  }
  // One nested value is enough: a field that is sometimes an object is not a
  // measure, and must not become editable.
  if (nested > 0) return 'nested';
  if (numbers === 0 && texts === 0) return 'none';
  // Mostly-text wins: one stray numeric code in a usage column is not a measure.
  return texts > numbers ? 'text' : 'number';
}

/** Classify every discovered field once, for a whole source. */
export function classifyAll(fields: readonly string[], rows: readonly DataRow[]): Map<string, MeasureClass> {
  const out = new Map<string, MeasureClass>();
  for (const f of fields) out.set(f, classifyField(f, { sample: rows }));
  return out;
}

export function isMeasurable(field: string, cls: MeasureClass): boolean {
  return cls !== 'text' && cls !== 'nested' && !NON_MEASURE_FIELDS.has(field.toLowerCase());
}

/** A nested value can be shown but never aggregated, and never written back. */
export function isWritable(cls: MeasureClass): boolean {
  return cls !== 'nested';
}

/**
 * A readable one-line summary of a nested value, for a cell. Deliberately a
 * SUMMARY: the cell is a view of the object, not the object, which is why
 * editing one cannot be pushed back.
 */
export function summariseNested(value: unknown, limit = 4): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    const shown = value.slice(0, limit).map((v) => summariseScalar(v));
    return `${shown.join(', ')}${value.length > limit ? `, +${value.length - limit}` : ''}`;
  }
  if (typeof value !== 'object') return String(value);
  const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== null && v !== '');
  const shown = entries.slice(0, limit).map(([k, v]) => `${k} ${summariseScalar(v)}`);
  return `${shown.join(' · ')}${entries.length > limit ? ` · +${entries.length - limit}` : ''}`;
}

function summariseScalar(v: unknown): string {
  if (typeof v === 'number') {
    // A mix is usually a fraction; showing 0.35 as 35% is what a schedule wants.
    if (v > 0 && v <= 1) return `${Math.round(v * 100)}%`;
    return String(Number(v.toFixed(2)));
  }
  if (v !== null && typeof v === 'object') return '…';
  return String(v);
}

/** Human label for a raw property key: `grossArea` -> `Gross Area`. */
export function prettyField(field: string): string {
  return field
    .replace(/^_/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}
